export function parsePositiveNumber(value, fallback = null) {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) return num;
  return fallback;
}

export function parseNonNegativeNumber(value, fallback) {
  const num = Number(value);
  if (Number.isFinite(num) && num >= 0) return num;
  return fallback;
}

export function buildCombatTimer(runtime, timestamp) {
  if (!runtime) return null;

  const interval = parsePositiveNumber(runtime?.attackInterval ?? runtime?.AttackInterval)
    ?? parsePositiveNumber(runtime?.nextAttackTimer ?? runtime?.NextAttackTimer);
  if (!interval) return null;

  const remainingRaw = parseNonNegativeNumber(
    runtime?.nextAttackTimer ?? runtime?.NextAttackTimer,
    interval,
  );
  const remaining = Number.isFinite(remainingRaw) ? Math.max(0, remainingRaw) : interval;
  const nextAttackAt = timestamp + remaining * 1000;
  const lastAttackAt = nextAttackAt - interval * 1000;

  return {
    interval,
    remaining,
    updatedAt: timestamp,
    lastAttackAt,
    nextAttackAt,
  };
}

export function localizeServerTimestamp(serverTimestamp, sentAtMs, receivedAtMs) {
  const serverTime = Number(serverTimestamp);
  const sentAt = Number(sentAtMs);
  const receivedAt = Number(receivedAtMs);
  if (!Number.isFinite(serverTime) || !Number.isFinite(sentAt) || !Number.isFinite(receivedAt)) {
    return null;
  }
  return serverTime + (receivedAt - sentAt);
}

export function buildCombatTimerFromEvent(details, envelope, fallbackTimer = null) {
  const receivedAtMs = Number(envelope?.receivedAtMs);
  const now = Number.isFinite(receivedAtMs) ? receivedAtMs : Date.now();
  const intervalMs = parsePositiveNumber(details?.attackIntervalMs)
    ?? (parsePositiveNumber(fallbackTimer?.interval) * 1000);
  if (!intervalMs) return fallbackTimer;

  const interval = intervalMs / 1000;
  const localizedNextAttackAt = localizeServerTimestamp(
    details?.nextAttackAtMs,
    envelope?.sentAtMs,
    now,
  );
  const nextAttackAt = Number.isFinite(localizedNextAttackAt)
    ? localizedNextAttackAt
    : now + intervalMs;

  return {
    interval,
    remaining: Math.max(0, (nextAttackAt - now) / 1000),
    updatedAt: now,
    lastAttackAt: nextAttackAt - intervalMs,
    nextAttackAt,
  };
}

export function buildFleeTimerFromState(fleeState, envelope = {}) {
  if (!fleeState) return null;

  const intervalMs = parsePositiveNumber(
    fleeState.attemptIntervalMs ?? fleeState.AttemptIntervalMs,
  );
  if (!intervalMs) return null;

  const receivedAtMs = Number(envelope.receivedAtMs);
  const now = Number.isFinite(receivedAtMs) ? receivedAtMs : Date.now();
  const localizedLastAttemptAt = localizeServerTimestamp(
    fleeState.lastTickMs ?? fleeState.LastTickMs,
    envelope.sentAtMs,
    now,
  );
  const lastAttemptAt = Number.isFinite(localizedLastAttemptAt)
    ? localizedLastAttemptAt
    : now;
  const nextAttemptAt = lastAttemptAt + intervalMs;

  return {
    interval: intervalMs / 1000,
    remaining: Math.max(0, (nextAttemptAt - now) / 1000),
    updatedAt: now,
    lastAttackAt: lastAttemptAt,
    nextAttackAt: nextAttemptAt,
  };
}

export function computeCountdown(timer, tick) {
  if (!timer) return null;
  const interval = parsePositiveNumber(timer.interval);
  if (!interval) return null;

  const intervalMs = interval * 1000;
  const updatedAt = Number(timer.updatedAt);
  const baseRemaining = Number.isFinite(timer.remaining) ? Math.max(0, timer.remaining) : interval;

  const fallbackNextAttackAt = Number.isFinite(timer.nextAttackAt)
    ? timer.nextAttackAt
    : (Number.isFinite(updatedAt) ? updatedAt + baseRemaining * 1000 : tick + baseRemaining * 1000);

  const nextAttackAt = fallbackNextAttackAt;
  const lastAttackAt = Number.isFinite(timer.lastAttackAt)
    ? timer.lastAttackAt
    : nextAttackAt - intervalMs;

  const now = tick;
  const remainingMs = Math.max(0, nextAttackAt - now);
  const remaining = remainingMs / 1000;
  const elapsedMs = Math.max(0, Math.min(intervalMs, now - lastAttackAt));
  const filled = Math.min(interval, elapsedMs / 1000);
  const progress = intervalMs > 0 ? Math.min(1, elapsedMs / intervalMs) : 0;

  return {
    remaining,
    interval,
    progress,
    filled,
  };
}

export function formatCountdownLabel(seconds) {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds <= 0) return 'Ready';
  if (seconds < 0.1) return `${Math.max(1, Math.round(seconds * 1000))}ms`;
  if (seconds < 1) return `${seconds.toFixed(2)}s`;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
}
